import { apiCache } from './apiCache';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8787';

// Optional global refresh function registered by an Auth0-aware hook/provider
let globalTokenRefreshFn: (() => Promise<string>) | null = null;
export function setTokenRefreshFunction(fn: (() => Promise<string>) | null) {
  globalTokenRefreshFn = fn;
}

// Export cache for manual invalidation
export { apiCache } from './apiCache';

class ApiClient {
  private isErrorPayload(value: unknown): value is { error: unknown } {
    if (!value || typeof value !== 'object') return false;
    if (!('error' in (value as any))) return false;
    const keys = Object.keys(value as any);
    // treat "{ error: ... }" (and nothing else) as an error payload
    return keys.length === 1;
  }

  private getHeaders(): HeadersInit {
    const token = localStorage.getItem('auth_token');
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
    };

    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    return headers;
  }

  async request<T>(endpoint: string, options: RequestInit = {}, useCache = false): Promise<T> {
    const method = options.method || 'GET';
    const cacheKey = `${method}:${endpoint}`;

    const staleCached = useCache && method === 'GET' ? apiCache.getStale<T>(cacheKey) : null;
    
    // Try cache for GET requests if caching is enabled
    if (useCache && method === 'GET') {
      const cached = apiCache.get<T>(cacheKey);
      if (cached !== null) {
        return cached;
      }
    }
    
    console.log('[API] Request:', endpoint, method);

    let response = await fetch(`${API_BASE_URL}${endpoint}`, {
      ...options,
      credentials: 'include',
      headers: {
        ...this.getHeaders(),
        ...options.headers,
      },
    });

    console.log('[API] Response:', endpoint, 'Status:', response.status, 'OK:', response.ok);

    // If unauthorized, attempt a single silent refresh via registered function and retry once
    if (response.status === 401) {
      console.warn('[API] 401 received for', endpoint, '- attempting silent refresh');
      if (globalTokenRefreshFn) {
        try {
          const newToken = await globalTokenRefreshFn();
          localStorage.setItem('auth_token', newToken);
          // retry once
          response = await fetch(`${API_BASE_URL}${endpoint}`, {
            ...options,
            credentials: 'include',
            headers: {
              ...this.getHeaders(),
              ...options.headers,
            },
          });
          console.log('[API] Retry response:', endpoint, 'Status:', response.status, 'OK:', response.ok);
        } catch (e) {
          console.error('[API] Silent refresh failed:', e);
          // fall through
        }
      }
    }

    if (response.status === 401) {
      // If we have a stale cached value for this GET, serve it instead of logging out.
      if (method === 'GET' && useCache && staleCached !== null) {
        console.warn('[API] Serving stale cached response for', endpoint, 'due to 401');
        return staleCached;
      }

      // Never auto-clear local auth state here; it causes frustrating "random logouts".
      // Let pages/route guards decide how to handle persistent Unauthorized states.
      console.warn('[API] Final 401 after refresh for', endpoint, '- returning Unauthorized (no auto logout)');
      throw new Error('Unauthorized');
    }

    if (!response.ok) {
      const error:any = await response.json().catch(() => ({ error: 'Unknown error' }));
      console.error('[API] Error response:', endpoint, 'Status:', response.status, 'Body:', error);

      // If this is a GET and we have stale cached data, use it as a fallback.
      if (method === 'GET' && useCache && staleCached !== null) {
        console.warn('[API] Serving stale cached response for', endpoint, 'due to error status', response.status);
        return staleCached;
      }

      throw new Error(error.error || `HTTP ${response.status}`);
    }

    const data = await response.json().catch(() => null);

    // Some upstream paths have returned 200 with an error-shaped body; treat it as failure.
    if (this.isErrorPayload(data)) {
      const message = (data as any)?.error || 'Unknown error';
      console.error('[API] 200-with-error payload for', endpoint, 'Body:', data);

      if (method === 'GET' && useCache && staleCached !== null) {
        console.warn('[API] Serving stale cached response for', endpoint, 'due to 200-with-error payload');
        return staleCached;
      }

      throw new Error(String(message));
    }
    
    // Cache successful GET responses if caching is enabled
    if (useCache && method === 'GET') {
      apiCache.set(cacheKey, data);
    }
    
    return data;
  }

  // Doctor endpoints
  async getDashboard() {
    return this.request('/api/doctor/dashboard?format=json', {}, true);
  }

  async getPatients() {
    return this.request('/api/doctor/patients', {}, true);
  }

  async getPatient(id: number) {
    return this.request(`/api/doctor/patients/${id}`, {}, true);
  }

  async createPatient(data: any) {
    const result = await this.request('/api/doctor/patients', {
      method: 'POST',
      body: JSON.stringify(data),
    });
    // Invalidate patients cache (both doctor and parent views)
    apiCache.invalidatePattern('/api/doctor/patients');
    apiCache.invalidate('GET:/api/doctor/dashboard?format=json');
    apiCache.invalidatePattern('/api/parent'); // Parent might have this patient
    return result;
  }

  async updatePatient(id: number, data: any) {
    const result = await this.request(`/api/doctor/patients/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
    // Invalidate affected caches (both doctor and parent views)
    apiCache.invalidate(`GET:/api/doctor/patients/${id}`);
    apiCache.invalidatePattern('/api/doctor/patients');
    apiCache.invalidate('GET:/api/doctor/dashboard?format=json');
    apiCache.invalidatePattern('/api/parent'); // Parent might see this patient
    return result;
  }

  async deletePatient(id: number) {
    const result = await this.request(`/api/doctor/patients/${id}`, {
      method: 'DELETE',
    });
    // Invalidate affected caches (both doctor and parent views)
    apiCache.invalidate(`GET:/api/doctor/patients/${id}`);
    apiCache.invalidatePattern('/api/doctor/patients');
    apiCache.invalidate('GET:/api/doctor/dashboard?format=json');
    apiCache.invalidatePattern('/api/parent'); // Remove deleted patient from parent cache
    return result;
  }

  async sendVaccineReminder(patientId: number, upcomingDoses: any[]) {
    const result = await this.request(`/api/doctor/patients/${patientId}/send-reminder`, {
      method: 'POST',
      body: JSON.stringify({ upcoming_doses: upcomingDoses }),
    });
    return result;
  }

  async logVaccineDose(patientId: number, data: any) {
    const result = await this.request(`/api/doctor/patients/${patientId}/doses`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
    // Invalidate affected caches
    apiCache.invalidate(`GET:/api/doctor/patients/${patientId}/doses`);
    apiCache.invalidate(`GET:/api/doctor/patients/${patientId}`);
    apiCache.invalidate('GET:/api/doctor/dashboard?format=json');
    return result;
  }

  async getVaccineHistory(patientId: number) {
    return this.request(`/api/doctor/patients/${patientId}/doses`, {}, true);
  }

  async overrideNextDose(doseId: number, data: any) {
    const result = await this.request(`/api/doctor/doses/${doseId}/override`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
    // Invalidate affected caches
    apiCache.invalidatePattern('/api/doctor/patients');
    apiCache.invalidate('GET:/api/doctor/dashboard?format=json');
    return result;
  }

  async deleteVaccineDose(doseId: number) {
    const result = await this.request(`/api/doctor/doses/${doseId}`, {
      method: 'DELETE',
    });
    // Invalidate affected caches
    apiCache.invalidatePattern('/api/doctor/patients');
    apiCache.invalidate('GET:/api/doctor/dashboard?format=json');
    return result;
  }

  async getVaccines() {
    return this.request('/api/doctor/vaccines', {}, true);
  }

  async createVaccine(data: any) {
    const result = await this.request('/api/doctor/vaccines', {
      method: 'POST',
      body: JSON.stringify(data),
    });
    apiCache.invalidate('GET:/api/doctor/vaccines');
    return result;
  }

  async updateVaccine(id: number, data: any) {
    const result = await this.request(`/api/doctor/vaccines/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
    apiCache.invalidate('GET:/api/doctor/vaccines');
    return result;
  }

  async deleteVaccine(id: number) {
    const result = await this.request(`/api/doctor/vaccines/${id}`, {
      method: 'DELETE',
    });
    apiCache.invalidate('GET:/api/doctor/vaccines');
    return result;
  }

  async uploadVaccineImage(vaccineId: number, file: File) {
    const token = localStorage.getItem('auth_token');
    const formData = new FormData();
    formData.append('image', file);

    const response = await fetch(`${API_BASE_URL}/api/doctor/vaccines/${vaccineId}/image`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        Authorization: `Bearer ${token}`,
      },
      body: formData,
    });

    if (!response.ok) {
      const error: any = await response.json().catch(() => ({ error: 'Upload failed' }));
      throw new Error(error.error || `HTTP ${response.status}`);
    }

    return response.json();
  }

  async deleteVaccineImage(vaccineId: number) {
    return this.request(`/api/doctor/vaccines/${vaccineId}/image`, {
      method: 'DELETE',
    });
  }

  async getClinicById(id: number) {
    return this.request(`/api/doctor/clinic/${id}`, {}, true);
  }

  async updateClinic(id: number, data: { name?: string; contact_email?: string }) {
    const result = await this.request(`/api/doctor/clinic/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
    apiCache.invalidate(`GET:/api/doctor/clinic/${id}`);
    apiCache.invalidate('GET:/api/doctor/settings');
    return result;
  }

  async getClinicSettings() {
    return this.request('/api/doctor/settings', {}, true);
  }

  async updateClinicSettings(data: any) {
    const result = await this.request('/api/doctor/settings', {
      method: 'PUT',
      body: JSON.stringify(data),
    });
    apiCache.invalidate('GET:/api/doctor/settings');
    return result;
  }

  async getDoctorProfile() {
    return this.request('/api/doctor/profile', {}, true);
  }

  async updateDoctorProfile(data: { first_name?: string; last_name?: string }) {
    const result = await this.request('/api/doctor/profile', {
      method: 'PUT',
      body: JSON.stringify(data),
    });
    apiCache.invalidate('GET:/api/doctor/profile');
    return result;
  }

  // Visits endpoints
  async getPatientVisits(patientId: number) {
    return this.request(`/api/doctor/patients/${patientId}/visits`, {}, true);
  }

  async getRecentVisits(limit = 10) {
    return this.request(`/api/doctor/visits?limit=${limit}`, {}, true);
  }

  async getVisit(visitId: number) {
    return this.request(`/api/doctor/visits/${visitId}`, {}, true);
  }

  async createVisit(data: {
    patient_id: number;
    visit_date: string;
    weight_kg?: number;
    height_cm?: number;
    doctor_notes?: string;
    doses?: Array<{ vaccine_id: number; dose_number: number }>;
    custom_vitals?: Array<{ field_name: string; field_value: string; field_unit?: string }>;
  }) {
    const result = await this.request('/api/doctor/visits', {
      method: 'POST',
      body: JSON.stringify(data),
    });
    // Invalidate affected caches
    apiCache.invalidate(`GET:/api/doctor/patients/${data.patient_id}/visits`);
    apiCache.invalidate(`GET:/api/doctor/patients/${data.patient_id}`);
    apiCache.invalidatePattern('/api/doctor/visits');
    apiCache.invalidate('GET:/api/doctor/dashboard?format=json');
    return result;
  }

  async updateVisit(visitId: number, data: {
    weight_kg?: number;
    height_cm?: number;
    doctor_notes?: string;
    custom_vitals?: Array<{ field_name: string; field_value: string; field_unit?: string }>;
  }) {
    const result = await this.request(`/api/doctor/visits/${visitId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
    apiCache.invalidatePattern('/api/doctor/visits');
    apiCache.invalidatePattern('/api/doctor/patients');
    return result;
  }

  async deleteVisit(visitId: number) {
    const result = await this.request(`/api/doctor/visits/${visitId}`, {
      method: 'DELETE',
    });
    apiCache.invalidatePattern('/api/doctor/visits');
    apiCache.invalidatePattern('/api/doctor/patients');
    apiCache.invalidate('GET:/api/doctor/dashboard?format=json');
    return result;
  }

  async getClinicVitalFields() {
    return this.request('/api/doctor/vital-fields', {}, true);
  }

  async addClinicVitalField(data: {
    field_name: string;
    field_label: string;
    field_type?: string;
    field_unit?: string;
  }) {
    const result = await this.request('/api/doctor/vital-fields', {
      method: 'POST',
      body: JSON.stringify(data),
    });
    apiCache.invalidate('GET:/api/doctor/vital-fields');
    return result;
  }

  // Parent endpoints
  async parentLogin(email: string) {
    return this.request('/api/auth/parent/login', {
      method: 'POST',
      body: JSON.stringify({ email }),
    });
  }

  async parentVerify(email: string, pin: string) {
    return this.request('/api/auth/parent/verify', {
      method: 'POST',
      body: JSON.stringify({ email, pin }),
    });
  }

  async getParentDashboard() {
    return this.request('/api/parent/dashboard');
  }

  async getParentPatient(id: number) {
    return this.request(`/api/parent/patients/${id}`);
  }

  async getParentPatientVisits(patientId: number) {
    return this.request(`/api/parent/patients/${patientId}/visits`);
  }

  async updateParentContact(data: any) {
    return this.request('/api/parent/contact', {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async downloadVaccinePassport(patientId: number) {
    const token = localStorage.getItem('auth_token');
    // Include user's timezone so the PDF timestamp is in local time
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const response = await fetch(`${API_BASE_URL}/api/parent/patients/${patientId}/passport?tz=${encodeURIComponent(timezone)}`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      throw new Error('Failed to download passport');
    }

    return response.blob();
  }
}

export const api = new ApiClient();
